import React, { useCallback, useEffect } from 'react';
import { Tag, Text } from '@goodfynd/react-web.lib.ui';

import config from '../../config';
import strings from '../../config/strings';
import { useStores } from '../../stores';
import type { OrderingOptionGroup } from '../../types/products';
import { numberUtil } from '../../utils';
import { InputCheckboxListControl } from '../forms';
import InputRadioGroupControl from '../forms/InputRadioGroup/InputRadioGroupControl';

import {
  GroupNameContainer,
  StyledProductOptionsContainer,
  StyledProductOptionsContainerDisabled,
  StyledProductOptionsGroup,
} from './styles';
import type { ProductOptionsGroupProps } from './types';

const ProductOptionsGroup = ({
  groups,
  invalidGroup,
  refs,
}: ProductOptionsGroupProps) => {
  useEffect(() => {
    // Initialize ref for each option group
    refs.current = groups.reduce((acc, item) => {
      acc[item.id] = React.createRef<HTMLDivElement>();
      return acc;
    }, {} as Record<string, React.RefObject<HTMLDivElement>>);
  }, [groups, refs]);

  const {
    orderStore: { event, request, updateOptions },
  } = useStores();

  const getGroupLimit = useCallback((group: OrderingOptionGroup) => {
    const limit = group.limit ?? group.allowMultiple ? 0 : 1;
    const hasLimit =
      (group.allowMultiple && limit) ||
      (!group.allowMultiple && !group.required);
    return { hasLimit, limit };
  }, []);

  const renderGroupLabel = useCallback(
    (group: OrderingOptionGroup) => {
      const { hasLimit, limit } = getGroupLimit(group);
      return (
        <>
          <GroupNameContainer>
            <Text as="subhead2">{group.name}</Text>

            {group.required ? (
              <Tag
                highlight={group.id === invalidGroup}
                label={strings.labels.required}
                type="important"
              />
            ) : (
              <Tag label={strings.labels.optional} />
            )}
          </GroupNameContainer>

          {hasLimit && (
            <Text as="body2">
              {limit > 1 ? `Choose up to ${limit}` : 'Choose only one'}
            </Text>
          )}
        </>
      );
    },
    [getGroupLimit, invalidGroup]
  );

  const renderCheckboxes = useCallback(
    (group: OrderingOptionGroup) => (
      <StyledProductOptionsGroup key={group.id} ref={refs.current[group.id]}>
        <InputCheckboxListControl<string>
          label={renderGroupLabel(group)}
          max={getGroupLimit(group).limit}
          name={group.name}
          defaultValue={request?.optionGroups
            .find(({ id }) => id === group.id)
            ?.options.map((option) => option.value)}
          options={group.options
            .filter((option) => !option.isDeleted)
            .sort((option1, option2) => {
              const price1 = option1.price || 0;
              const price2 = option2.price || 0;

              // sort by price
              if (price1 < price2) {
                return 1;
              } else if (price1 > price2) {
                return -1;
              }

              // then by name
              if (option1.name < option2.name) {
                return -1;
              }
              if (option1.name > option2.name) {
                return 1;
              }
              return 0;
            })
            .map((option) => ({
              label: option.name,
              labelRight:
                option.price !== 0
                  ? `${
                      (option.price || 0) > 0
                        ? config.unicode.plus
                        : config.unicode.minus
                    } ${numberUtil.renderPrice(Math.abs(option.price || 0))}`
                  : undefined,
              value: option.value,
            }))}
          setValue={(_name, options) =>
            updateOptions(
              group.id,
              group.options.filter((option) => options.includes(option.value))
            )
          }
        />
      </StyledProductOptionsGroup>
    ),
    [
      getGroupLimit,
      refs,
      renderGroupLabel,
      request?.optionGroups,
      updateOptions,
    ]
  );

  const renderRadios = useCallback(
    (group: OrderingOptionGroup) => {
      const options = request?.optionGroups.find(
        ({ id }) => id === group.id
      )?.options;
      return (
        <StyledProductOptionsGroup key={group.id} ref={refs.current[group.id]}>
          <InputRadioGroupControl
            defaultValue={options?.length && options[0].value}
            label={renderGroupLabel(group)}
            onChange={(_e, value) =>
              updateOptions(
                group.id,
                group.options.filter((option) => option.value === value)
              )
            }
            options={group.options
              .filter((option) => !option.isDeleted)
              .sort((option1, option2) => {
                const price1 = option1.price || 0;
                const price2 = option2.price || 0;

                // sort by price
                if (price1 < price2) {
                  return 1;
                } else if (price1 > price2) {
                  return -1;
                }

                // then by name
                if (option1.name < option2.name) {
                  return -1;
                }
                if (option1.name > option2.name) {
                  return 1;
                }
                return 0;
              })
              .map((option) => ({
                label: option.name,
                labelRight:
                  option.price !== 0
                    ? `${
                        (option.price || 0) > 0
                          ? config.unicode.plus
                          : config.unicode.minus
                      } ${numberUtil.renderPrice(Math.abs(option.price || 0))}`
                    : undefined,
                value: option.value,
              }))}
          />
        </StyledProductOptionsGroup>
      );
    },
    [refs, renderGroupLabel, request?.optionGroups, updateOptions]
  );

  return (
    <StyledProductOptionsContainer
      css={{
        opacity: !event?.pickupTime ? 0.45 : 1,
      }}
    >
      {groups.map((group) =>
        group.allowMultiple || !group.required
          ? renderCheckboxes(group)
          : renderRadios(group)
      )}

      {!event?.pickupTime && <StyledProductOptionsContainerDisabled />}
    </StyledProductOptionsContainer>
  );
};

export default ProductOptionsGroup;
